import { CoreDialogContent } from '@youscience/core';

import { IpedsCardInfo } from '@common-types';

import useSchoolSelectionStore, { STEPS } from '@store/SchoolSelectionStore';
import useHomePageStore from '@store/HomePageStore';
import { notify } from '@store/NotificationStore';

import DialogFooter from '@components/DialogFooter';

import { SelectionStep1 } from './components/SelectionStep1';
import { SelectionStep2 } from './components/SelectionStep2';
import { NoteMessage } from './components/NoteMessage';

import { sxStyles, StyledDialog, StyledDialogTitle } from './SchoolSelection.styles';

interface SchoolSelectionProps {
  isOpenSchoolSelection: boolean;
  onCloseSchoolSelection: () => void;
}

export const SchoolSelection = (props: SchoolSelectionProps) => {
  const { isOpenSchoolSelection, onCloseSchoolSelection } = props;

  const {
    activeStep = STEPS.step1,
    school,
    schoolPreviewInfo,
    isConfirmToSelection,
    isGrantAccessToYSProfile,
    tenantId,
  } = useSchoolSelectionStore((state) => state.schoolSelectionData);

  const { setSchoolSelectionData, saveSchoolSelection, grantAccessToYSProfile, isPreviewCardDataLoading } =
    useSchoolSelectionStore((state) => state);

  const { getLandingPageData } = useHomePageStore((state) => state);

  const handleConfirmSelection = async () => {
    try {
      onCloseSchoolSelection();

      if (isConfirmToSelection && isGrantAccessToYSProfile) {
        await grantAccessToYSProfile(schoolPreviewInfo, isGrantAccessToYSProfile);
      }

      await saveSchoolSelection();
      await getLandingPageData();
    } catch (err) {
      notify({ message: 'Error while saving school selection', severity: 'error' });
    }
  };

  const handleConfirmStep2 = () => {
    if (tenantId) {
      void handleConfirmSelection();

      return;
    }

    void setSchoolSelectionData({ activeStep: STEPS.noteMessage });
  };

  const handleGoToStep1 = () => {
    void setSchoolSelectionData({
      activeStep: STEPS.step1,
      schoolPreviewInfo: {} as IpedsCardInfo,
      school: null,
      isGrantAccessToYSProfile: false,
    });
  };

  const cancelDialog = () => {
    setSchoolSelectionData({ state: null, school: null, activeStep: STEPS.step1 });
    onCloseSchoolSelection();
  };

  const ENROLLING_STEPS_DATA = {
    [STEPS.noteMessage]: {
      title: 'Please Note!',
      content: <NoteMessage />,
      footer: (
        <DialogFooter
          goBackButtonTitle="Cancel"
          goNextButtonTitle="Confirm"
          goNextActionButtonTitle="Confirming"
          onGoBack={handleGoToStep1}
          onGoNext={handleConfirmSelection}
        />
      ),
    },
    [STEPS.step1]: {
      title: 'Congratulations on enrolling in school!',
      content: <SelectionStep1 />,
      footer: <DialogFooter goBackButtonTitle="Cancel" onGoBack={cancelDialog} />,
    },
    [STEPS.step2]: {
      title: `Confirm Enrollment at ${school?.label as string}`,
      content: <SelectionStep2 />,
      footer: (
        <DialogFooter
          goBackButtonTitle="Back"
          goNextButtonTitle="Confirm"
          goNextActionButtonTitle="Confirming"
          isDisableNextButton={!isConfirmToSelection || isPreviewCardDataLoading}
          onGoBack={handleGoToStep1}
          onGoNext={handleConfirmStep2}
        />
      ),
    },
  };

  return (
    <StyledDialog open={isOpenSchoolSelection} onClose={cancelDialog} scroll="paper">
      <StyledDialogTitle data-testid="schoolSelectionTitle" id="school-selection">
        {ENROLLING_STEPS_DATA[activeStep].title}
      </StyledDialogTitle>

      <CoreDialogContent sx={sxStyles.contentBody}>{ENROLLING_STEPS_DATA[activeStep].content}</CoreDialogContent>

      {ENROLLING_STEPS_DATA[activeStep].footer}
    </StyledDialog>
  );
};
